import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../game/game.css";
import { fetchThemeData } from "../../admin/themeSlice";
import { getDvSourceImageUrl, toCssUrlValue } from "../../functions/themeAssets";
import "./videoIntro.css";

const AMBIENT_GRADIENT_FALLBACK = [
  "radial-gradient(950px 660px at 10% -8%,  rgba(199,87,76,.26),  transparent 64%)",
  "radial-gradient(950px 660px at 92% 110%, rgba(46,139,103,.28), transparent 64%)",
  "radial-gradient(1150px 680px at 50% 34%, rgba(217,166,46,.20), transparent 66%)",
  "radial-gradient(700px 500px at 96% -4%,  rgba(46,139,103,.12), transparent 60%)",
  "radial-gradient(700px 500px at 4% 106%,  rgba(199,87,76,.12),  transparent 60%)",
].join(", ");

/** S3 intro video — REACT_APP_INTRO_VIDEO_URL, else REACT_APP_S3_PATH + intro.mp4 */
function getIntroVideoUrl() {
  const fromEnv = String(process.env.REACT_APP_INTRO_VIDEO_URL ?? "").trim();
  if (fromEnv) return fromEnv;
  const base = String(process.env.REACT_APP_S3_PATH ?? "").replace(/\/?$/, "/");
  return `${base}intro.mp4`;
}

const INTRO_VIDEO_SRC = getIntroVideoUrl();

export default function VideoIntro() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);
  const videoRef = useRef(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.matchMedia("(max-width: 640px)").matches
  );

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = () => setIsMobileViewport(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const backgroundImageUrl = useMemo(() => {
    if (!themeData) return "";
    const key = isMobileViewport ? themeData.background_mob : themeData.background_desk;
    return key ? getDvSourceImageUrl(key) : "";
  }, [themeData, isMobileViewport]);

  useEffect(() => {
    const targets = [document.documentElement, document.body];
    const image = backgroundImageUrl ? toCssUrlValue(backgroundImageUrl) : AMBIENT_GRADIENT_FALLBACK;
    for (const el of targets) {
      el.style.backgroundImage = image;
      el.style.backgroundSize = "cover";
      el.style.backgroundPosition = "top center";
      el.style.backgroundRepeat = "no-repeat";
      el.style.backgroundAttachment = "fixed";
    }
    return () => {
      for (const el of targets) {
        el.style.removeProperty("background-image");
        el.style.removeProperty("background-size");
        el.style.removeProperty("background-position");
        el.style.removeProperty("background-repeat");
        el.style.removeProperty("background-attachment");
      }
    };
  }, [backgroundImageUrl]);

  const themeVars = useMemo(
    () => ({
      "--theme-accent": themeData?.button_color || undefined,
      "--theme-accent-tint": themeData?.icon_bg_color || undefined,
      "--theme-text": themeData?.textcolor || undefined,
      "--theme-button-text": themeData?.button_Textcolor || undefined,
      "--theme-card-bg": themeData?.text_bg_color || undefined,
    }),
    [themeData]
  );

  const goToGame = useCallback(() => navigate("/game", { replace: true }), [navigate]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      video.muted = true;
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => goToGame());
      });
    };

    const onCanPlay = () => tryPlay();
    video.addEventListener("canplay", onCanPlay, { once: true });
    if (video.readyState >= 3) tryPlay();

    return () => video.removeEventListener("canplay", onCanPlay);
  }, [goToGame]);

  return (
    <div className="mf-root vi-root" style={themeVars}>
      <div className="vi-app">
        <div className="vi-stage">
          <div className="vi-player-box">
            <video
              ref={videoRef}
              className="vi-video"
              src={INTRO_VIDEO_SRC}
              controls
              autoPlay
              muted
              playsInline
              preload="auto"
              onEnded={goToGame}
              onError={goToGame}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
